import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef, ScrollDispatcher } from '@angular/cdk/overlay';
import { fromEvent } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { ComponentPortal } from '@angular/cdk/portal';
import { SkyUiPopoverComponent } from '../component/popover/popover';
import { Store } from '@skyeng/libs/shared/store';
import { filter } from 'rxjs/operators';

const DEFAULT_CONTENT_POSITION: ConnectedPosition = {
  overlayX: 'end',
  overlayY: 'top',
  originY: 'bottom',
  originX: 'end',
};

@DecorateUntilDestroy()
@Directive({
  selector: '[skyUiPopover]',
  exportAs: 'popover',
})
export class SkyUiPopoverDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('skyUiPopoverContent') content?: string;
  @Input('skyUiPopoverContentRef') contentRef?: TemplateRef<unknown>;
  @Input('skyUiPopoverContentRefContext') contentRefContext?: Object;
  @Input('skyUiPopoverHeaderContentRef') headerContentRef?: TemplateRef<unknown>;
  @Input('skyUiPopoverHeaderContentRefContext') headerContentRefContext?: Object;
  @Input('skyUiPopoverTitle') title?: string;
  @Input('skyUiPopoverTriggerElement') triggerElement?: HTMLElement;
  @Input('skyUiPopoverIsTriggeredManually') isTriggeredManually = false;
  @Input('skyUiPopoverDisabled') disabled?: boolean;
  @Input('skyUiPopoverTopOffset') topOffset?: number;
  @Input('skyUiPopoverPosition') position?: Partial<ConnectedPosition>;
  @Input('skyUiPopoverCloseOnClickOutside') closeOnClickOutside = true;
  @Input('skyUiPopoverFloatingName') floatingName?: string;
  @Input('skyUiPopoverClosingIcon') closingIcon? = false;
  @Input('skyUiPopoverBlur') blur? = false;
  @Input('skyUiPopoverViewportMargin') viewportMargin?: number;

  @Output('skyUiPopoverClosed') closed = new EventEmitter<void>();

  private overlayRef: OverlayRef;
  private portal: ComponentPortal<SkyUiPopoverComponent>;
  private popoverInstance?: SkyUiPopoverComponent | null;
  private store = new Store({ isOpen: false });

  isOpen$ = this.store.select('isOpen');

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private scrollDispatcher: ScrollDispatcher,
  ) {}

  ngAfterViewInit(): void {
    if (!this.triggerElement && this.isTriggeredManually) {
      return;
    }

    const hostElement = this.triggerElement || this.elementRef.nativeElement;

    fromEvent(hostElement, 'click')
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.onClick());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.content ||
      changes.contentRef ||
      changes.contentRefContext ||
      changes.headerContentRef ||
      changes.headerContentRefContext ||
      changes.title
    ) {
      this.updatePopoverInputs();
    }

    if (changes.disabled && this.disabled && this.isOpen()) {
      this.detach();
    }
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.detach();
      this.overlayRef.dispose();
    }
  }

  toggle(): void {
    this.onClick();
  }

  hide(): void {
    if (this.popoverInstance) {
      this.detach();
    }
  }

  show(): void {
    if (this.disabled || (!this.title && !this.contentRef && !this.content)) {
      return;
    }

    const overlayRef = this.createOverlay();

    if (!this.portal) {
      this.portal = new ComponentPortal(SkyUiPopoverComponent);
    }

    this.detach({ emitCloseEvent: false });

    this.popoverInstance = overlayRef.attach(this.portal).instance;

    this.popoverInstance.state$
      .pipe(
        filter(state => state === 'closed'),
        takeUntilDestroyed(this, { destroyMethod: this.detach }),
      )
      .subscribe(() => this.store.updateFields({ isOpen: false }));

    this.popoverInstance.closed$
      .pipe(takeUntilDestroyed(this, { destroyMethod: this.detach }))
      .subscribe(() => this.detach());

    this.updatePopoverInputs();
    this.store.updateFields({ isOpen: true });
  }

  private createOverlay(): OverlayRef {
    if (this.overlayRef) {
      return this.overlayRef;
    }

    const position: ConnectedPosition = { ...DEFAULT_CONTENT_POSITION, ...(this.position || {}) };
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(false)
      .withPositions([position]);

    if (this.viewportMargin) {
      positionStrategy.withViewportMargin(this.viewportMargin);
    }

    const scrollableAncestors = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef);

    positionStrategy.withScrollableContainers(scrollableAncestors);

    this.overlayRef = this.overlay.create({
      positionStrategy,
    });

    return this.overlayRef;
  }

  private isOpen(): boolean {
    return this.store.get('isOpen');
  }

  private updatePopoverInputs(): void {
    if (!this.popoverInstance) {
      return;
    }

    this.popoverInstance.setOptions({
      contentRef: this.contentRef,
      contentRefContext: this.contentRefContext,
      headerContentRef: this.headerContentRef,
      headerContentRefContext: this.headerContentRefContext,
      content: this.content,
      title: this.title,
      topOffset: this.topOffset,
      closeOnClickOutside: this.closeOnClickOutside,
      floatingName: this.floatingName,
      closingIcon: this.closingIcon,
      blur: this.blur,
    });
  }

  private detach({ emitCloseEvent } = { emitCloseEvent: true }): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }

    this.popoverInstance = null;
    this.store.updateFields({ isOpen: false });

    if (emitCloseEvent) {
      this.closed.emit();
    }
  }

  private onClick(): void {
    if (this.popoverInstance) {
      this.detach();
    } else {
      this.show();
    }
  }
}
